<script setup lang="ts">
  import { onMounted, onUnmounted, reactive } from "vue";
  import { useI18n } from "vue-i18n";
  import { Spinner, useNotification, IconScanAlarm } from "@ui-kit";
  import { LOCK_MEM_TYPE, usePowerRfid, useReadRfid, useWriteRfid, useSound, SOUNDS } from "@plugins";
  import {
    ScanScreen,
    ScanScreenStart,
    ScanScreenSuccess,
    ScanModal,
    ProductShortCard,
    WriteAlarmSuccess,
  } from "@src/components";
  import { useSpinner, useProfileOptions } from "@src/hooks";
  import { useGetWTFIR, WTFIRInputType, WTFIRTypeData, useLazyGetPairAlarm } from "@src/api";
  import { sleep } from "@src/utils";

  const emit = defineEmits<{ (e: "close"): void }>();

  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const writeRfid = useWriteRfid();
  const sound = useSound();
  const { execute: getWTFIR, isLoading: isWTFIRLoading } = useGetWTFIR();
  const { visible: visibleSpinner } = useSpinner([isWTFIRLoading]);
  const notification = useNotification();
  const { t } = useI18n();
  const { profile } = useProfileOptions();
  const { execute: getPairAlarm, data: pairAlarm } = useLazyGetPairAlarm();

  const state = reactive<{
    productId: string | null;
    stage: "scan" | "write";
    status: "start" | "success" | "error";
    epc: string | null;
  }>({
    productId: null,
    stage: "scan",
    status: "start",
    epc: null,
  });

  const handleError = (error: { message: string; type: string }) => {
    if (error.message) notification.show({ title: error.message, type: error.type as "error" | "warning" });
    if (error.type === "error") sound.playSound(SOUNDS.AUDIO_ERROR_DEFAULT);
  };

  const handleEpc = async (scanEpcData: string) => {
    try {
      if (!scanEpcData) throw { message: t("EPC метка не найдена"), type: "error" };

      const resultWTFIR = await getWTFIR({ data: scanEpcData, input_type: WTFIRInputType.rfid });

      if (resultWTFIR.isError.value) throw { message: t("Ошибка, попробуйте еще раз"), type: "error" };
      if (resultWTFIR.data.value.type_data !== WTFIRTypeData.epc)
        throw { message: t("Некорректный EPC"), type: "error" };
      if (resultWTFIR.data.value.items.length === 0)
        throw { message: t("Товар с данной EPC не найден"), type: "error" };

      state.productId = resultWTFIR.data.value.items.at(0);

      // подготавливаем данные для записи аларма
      const resultPairAlarm = await getPairAlarm({ epc: scanEpcData });
      if (resultPairAlarm.isError.value) throw { message: t("Ошибка, попробуйте еще раз"), type: "error" };
      powerRfid.set(profile.value.constants.rfid.power_alarm_read);

      state.epc = scanEpcData;
      state.status = "success";
      notification.clear();
      await sleep(300);

      state.stage = "write";
      state.status = "start";
    } catch (error) {
      handleError(error);
    }
  };

  const handleAlarm = async (scanAlarmData: string) => {
    try {
      if (!scanAlarmData) throw { message: t("Аларм не найден"), type: "error" };

      if (pairAlarm.value.epc_alarm === scanAlarmData) {
        state.status = "success";
      } else {
        const resultWTFIR = await getWTFIR({ data: scanAlarmData, input_type: WTFIRInputType.rfid });

        if (resultWTFIR.isError.value) throw { message: t("Ошибка, попробуйте еще раз"), type: "error" };
        if (![WTFIRTypeData.epcAlarm, WTFIRTypeData.epcAlarmClear].includes(resultWTFIR.data.value.type_data))
          throw { message: t("Некорректный Аларм"), type: "error" };

        powerRfid.set(profile.value.constants.rfid.power_alarm_read);

        // определяем пароль в зависимости от типа аларма
        let password = resultWTFIR.data.value.type_data === WTFIRTypeData.epcAlarm ? pairAlarm.value.key : undefined;

        // записываем аларм
        const resultWriteRfidData = await writeRfid.writeData({
          epc: pairAlarm.value.epc_alarm,
          password,
          filterData: scanAlarmData,
        });

        // если не удалось записать аларм с паролем, то пытаемся записать без пароля
        if (!resultWriteRfidData) {
          const resultWriteRfidDataWithEmptyPassword = await writeRfid.writeData({
            epc: pairAlarm.value.epc_alarm,
            filterData: scanAlarmData,
          });

          if (!resultWriteRfidDataWithEmptyPassword) throw { message: t("Ошибка записи аларма"), type: "error" };
          password = undefined;
        }

        // записываем пароль, если запись прошла без пароля
        if (!password) {
          const resultWriteRfidPassword = await writeRfid.writePassword({
            filterData: pairAlarm.value.epc_alarm,
            newPassword: pairAlarm.value.key,
          });
          if (!resultWriteRfidPassword) throw { message: t("Ошибка записи буфера"), type: "error" };
        }

        // блокируем блоки памяти
        const resultLockMem = await writeRfid.lockMem({
          password: pairAlarm.value.key,
          filterData: pairAlarm.value.epc_alarm,
          type: LOCK_MEM_TYPE.ALL,
        });
        if (!resultLockMem) throw { message: t("Ошибка блокировки памяти"), type: "error" };

        notification.clear();
        state.status = "success";
        powerRfid.set(profile.value.constants.rfid.power_once_read);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleScanRfid = async (scanRfidData: string) => {
    if (state.epc) {
      handleAlarm(scanRfidData);
    } else {
      handleEpc(scanRfidData);
    }
  };

  const handleClickMore = () => {
    state.productId = null;
    state.epc = null;
    state.stage = "scan";
    state.status = "start";
  };

  onMounted(() => {
    powerRfid.set(profile.value.constants.rfid.power_once_read);

    readRfid.subscribe((data) => {
      handleScanRfid(data.value);
    });
  });
  onUnmounted(() => {
    readRfid.unsubscribe();
  });
</script>

<template>
  <div class="widget__write-alarm" data-testid="widget-write-alarm">
    <ScanScreen
      v-if="state.stage === 'scan'"
      :modal="{ subtitle: $t('EPC алармированного товара') }"
      @close="emit('close')"
    >
      <Spinner v-if="visibleSpinner" style="width: 44px; height: 44px" />
      <ScanScreenStart v-else-if="state.status === 'start'" />
      <ScanScreenSuccess v-else-if="state.status === 'success'" />
    </ScanScreen>
    <div v-else-if="state.stage === 'write' && state.status === 'start'" class="write-alarm" data-testid="write-alarm">
      <ProductShortCard :productId="state.productId" @click:back="emit('close')" />
      <ScanModal
        :title="$t('Приложите Аларм')"
        :subtitle="$t('нажмите кнопку для записи')"
        :btnTitle="$t('Отмена')"
        @click:btn="emit('close')"
        @click:back="handleClickMore"
        data-testid="scan-alarm"
      >
        <template #icon>
          <IconScanAlarm />
        </template>
      </ScanModal>
    </div>
    <WriteAlarmSuccess
      v-else-if="state.stage === 'write' && state.status === 'success'"
      @click:more="handleClickMore"
      @click:cancel="emit('close')"
      data-testid="write-alarm-success"
    />
  </div>
</template>

<style scoped>
  .widget__write-alarm {
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  .write-alarm {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
</style>
